/**
 * Account API – self-service profile, password, profile image.
 */

import { rewriteLocalPresignedS3Url } from '@utils/localS3Presign';
import type {
  CancelEmailChangeResponse,
  Change2FAConfirmRequest,
  Change2FAConfirmResponse,
  Change2FASendCodeRequest,
  Change2FASendCodeResponse,
  Change2FASetupRequest,
  Change2FAVerifyCurrentRequest,
  Change2FAVerifyCurrentResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  Enable2FAResponse,
  RequestEmailChangeRequest,
  RequestEmailChangeResponse,
  UpdateMeRequest,
  UpdateMeResponse,
  User,
} from '@lambda/shared/types';
import { apiFetchUrl } from '@constants/apiConfig';
import { apiRequest } from './apiClient';

export function resolveProfileImageSrc(
  userId: string,
  profileImageUrl?: string | null,
  /** Bust browser cache when profile image changes (e.g. user.updatedAt). */
  cacheKey?: number
): string | undefined {
  if (!profileImageUrl) return undefined;
  if (profileImageUrl.startsWith('http')) {
    return cacheKey != null ? `${profileImageUrl}${profileImageUrl.includes('?') ? '&' : '?'}t=${cacheKey}` : profileImageUrl;
  }
  const path = profileImageUrl.startsWith('/') ? profileImageUrl : `/users/${userId}/profile-image`;
  const url = apiFetchUrl(path);
  return cacheKey != null ? `${url}?t=${cacheKey}` : url;
}

export const accountApi = {
  updateMe(accessToken: string, data: UpdateMeRequest) {
    return apiRequest<UpdateMeResponse>('/users/me', accessToken, {
      method: 'PUT',
      payload: data,
    });
  },

  changePassword(accessToken: string, data: ChangePasswordRequest) {
    return apiRequest<ChangePasswordResponse>('/users/me/change-password', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  requestEmailChange(accessToken: string, data: RequestEmailChangeRequest) {
    return apiRequest<RequestEmailChangeResponse>('/users/me/request-email-change', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  cancelEmailChange(accessToken: string) {
    return apiRequest<CancelEmailChangeResponse>('/users/me/cancel-email-change', accessToken, {
      method: 'POST',
      payload: {},
    });
  },

  change2FASendCode(accessToken: string, data: Change2FASendCodeRequest) {
    return apiRequest<Change2FASendCodeResponse>('/users/me/change-2fa/send-code', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  change2FAVerifyCurrent(accessToken: string, data: Change2FAVerifyCurrentRequest) {
    return apiRequest<Change2FAVerifyCurrentResponse>(
      '/users/me/change-2fa/verify-current',
      accessToken,
      { method: 'POST', payload: data }
    );
  },

  change2FASetup(accessToken: string, data: Change2FASetupRequest) {
    return apiRequest<Enable2FAResponse>('/users/me/change-2fa/setup', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  change2FAConfirm(accessToken: string, data: Change2FAConfirmRequest) {
    return apiRequest<Change2FAConfirmResponse>('/users/me/change-2fa/confirm', accessToken, {
      method: 'POST',
      payload: data,
    });
  },

  profileImageUploadRequest(accessToken: string, fileType: string, fileSize: number) {
    return apiRequest<{ uploadUrl: string; fileKey: string; message: string }>(
      '/users/me/profile-image/upload-request',
      accessToken,
      { method: 'POST', payload: { fileType, fileSize } }
    );
  },

  profileImageConfirm(accessToken: string) {
    return apiRequest<{ user: User; message: string }>('/users/me/profile-image/confirm', accessToken, {
      method: 'POST',
      payload: {},
    });
  },

  profileImageRemove(accessToken: string) {
    return apiRequest<{ user: User; message: string }>('/users/me/profile-image', accessToken, {
      method: 'DELETE',
    });
  },

  async uploadToPresignedUrl(uploadUrl: string, blob: Blob, contentType: string): Promise<void> {
    const targetUrl = rewriteLocalPresignedS3Url(uploadUrl);
    try {
      const res = await fetch(targetUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: blob,
      });
      if (!res.ok) {
        throw new Error('Upload failed. Check S3 CORS for local development.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('fetch')) {
        throw new Error(
          'Could not reach local S3. Run: npm run dev:s3 && npm run setup:local-s3. With a tunnel, use VITE_API_BASE_URL=same-origin and restart npm run dev:network. See docs/DEV-NETWORK.md and docs/S3-LOCAL-SETUP-GUIDE.md.',
          { cause: err }
        );
      }
      throw err;
    }
  },
};
